import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { 
    ScrollText, 
    FileText, 
    Search, 
    ArrowLeft, 
    Calendar,
    HardDrive,
    RefreshCw
} from 'lucide-react';
import { serverStore } from '../../stores/server.store';
import { userStore } from '../../stores/user.store';
import { apiFetch } from '../../utils/api';
import { cn } from '../../utils/cn';
import { formatBytes, formatDate } from '../../utils/format';
import { FunLoader } from '../common/FunLoader';
import toast from 'react-hot-toast';
import { can } from '@/utils/permissions';

interface LogFile {
    name: string;
    size: number;
    modified: string;
    isCompressed: boolean;
}

const LINE_HEIGHT = 24;
const BUFFER_LINES = 20;

export const ServerLogsTab = observer(() => {
    const { activeServer } = serverStore;
    const { currentUser } = userStore;
    const [logFiles, setLogFiles] = useState<LogFile[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState<string | null>(null);
    const [logContent, setLogContent] = useState<string[]>([]);
    const [loadingContent, setLoadingContent] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [contentSearch, setContentSearch] = useState('')
    const [scrollTop, setScrollTop] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const containerHeight = useRef(600);

    const canViewConsole = can(currentUser, 'can_view_console');
    const canModerate = can(currentUser, 'can_moderate_server');

    useEffect(() => {
        if (activeServer?.id) {
            fetchLogFiles();
        }
    }, [activeServer?.id]);

    const fetchLogFiles = async () => {
        if (!activeServer) return;
        setLoading(true);
        const startTime = Date.now();
        try {
            const res = await apiFetch(`/api/servers/${activeServer.id}/logs`);
            if (res.ok) {
                const data = await res.json();
                setLogFiles(data.logs || []);
            }
        } catch (e) {
            console.error('Failed to fetch log files', e);
            toast.error('Fehler beim Laden der Log-Dateien');
        } finally {
            const elapsed = Date.now() - startTime;
            const minDelay = 5000;
            if (elapsed < minDelay) {
                await new Promise(r => setTimeout(r, minDelay - elapsed));
            }
            setLoading(false);
        }
    };

    const viewLogContent = async (fileName: string) => {
        if (!activeServer) return;

        setLoadingContent(true);
        setLoadingProgress(0);
        setSelectedLog(fileName);
        setLogContent([]);
        const startTime = Date.now();

        try {
            setLoadingProgress(10);
            const res = await apiFetch(`/api/servers/${activeServer.id}/file?path=logs/${fileName}`);
            setLoadingProgress(40);
            
            if (res.ok) {
                const data = await res.json();
                setLoadingProgress(60);
                
                if (data.success && data.content) {
                    const lines = data.content.split('\n');
                    setLoadingProgress(80);
                    
                    const elapsed = Date.now() - startTime;
                    const minDelay = 3000;
                    if (elapsed < minDelay) {
                        const remaining = minDelay - elapsed;
                        const steps = 10;
                        for (let i = 0; i < steps; i++) {
                            await new Promise(r => setTimeout(r, remaining / steps));
                            setLoadingProgress(80 + Math.floor((i / steps) * 20));
                        }
                    }
                    setLogContent(lines);
                    setLoadingProgress(100);
                }
            }
        } catch (e) {
            console.error('Failed to load log content', e);
            toast.error('Fehler beim Laden des Log-Inhalts');
        } finally {
            setTimeout(() => setLoadingContent(false), 200);
        }
    };

    const filteredFiles = logFiles.filter(f => 
        f.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredContent = useMemo(() => {
        if (!contentSearch) return logContent;
        return logContent.filter(line => 
            line.toLowerCase().includes(contentSearch.toLowerCase())
        );
    }, [logContent, contentSearch]);

    const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(e.currentTarget.scrollTop);
    }, []);

    const visibleRange = useMemo(() => {
        const start = Math.max(0, Math.floor(scrollTop / LINE_HEIGHT) - BUFFER_LINES);
        const visibleCount = Math.ceil(containerHeight.current / LINE_HEIGHT) + BUFFER_LINES * 2;
        const end = Math.min(filteredContent.length, start + visibleCount);
        return { start, end };
    }, [scrollTop, filteredContent.length]);

    const getLineClass = useCallback((line: string) => {
        if (line.includes('[ERROR]') || line.includes('ERROR')) {
            return "border-red-500/50 bg-red-500/5 text-red-300";
        }
        if (line.includes('[WARN]') || line.includes('WARN')) {
            return "border-yellow-500/50 bg-yellow-500/5 text-yellow-300";
        }
        return "border-transparent hover:border-blue-500/50 hover:bg-white/5";
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            containerHeight.current = containerRef.current.clientHeight;
        }
    }, [selectedLog]);

    if (!canViewConsole && !canModerate) return null;

    if (selectedLog) {
        const totalHeight = filteredContent.length * LINE_HEIGHT;

        return (
            <div className="h-full flex flex-col glass-panel rounded-xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-300">
                <div className="bg-white/5 px-4 py-3 border-b border-line flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() => { setSelectedLog(null); setLogContent([]); setContentSearch(''); setScrollTop(0); }}
                            className="p-2 rounded-lg hover:bg-white/10 text-fg-muted hover:text-white transition-colors"
                        >
                            <ArrowLeft size={18} />
                        </button>
                        <div className="w-8 h-8 rounded-lg bg-black/40 flex items-center justify-center border border-line">
                            <FileText size={16} className="text-orange-400" />
                        </div>
                        <div>
                            <div className="text-xs text-fg-muted font-medium uppercase tracking-wider">Log Datei</div>
                            <div className="text-sm font-bold text-white">{selectedLog}</div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className="relative">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" />
                            <input
                                type="text"
                                value={contentSearch}
                                onChange={(e) => { setContentSearch(e.target.value); setScrollTop(0); }}
                                placeholder="Im Log suchen..."
                                className="bg-black/40 border border-line-strong rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-fg-muted/50 focus:outline-none focus:border-blue-500/50 w-64"
                            />
                        </div>
                        <span className="text-xs text-fg-muted bg-black/40 px-2 py-1 rounded border border-line">
                            {filteredContent.length.toLocaleString()} Zeilen
                        </span>
                    </div>
                </div>

                <div 
                    ref={containerRef}
                    onScroll={handleScroll}
                    className="flex-1 font-mono text-sm overflow-y-auto custom-scrollbar bg-black/40"
                >
                    {loadingContent ? (
                        <FunLoader showProgress progress={loadingProgress} />
                    ) : (
                        <div style={{ height: totalHeight, position: 'relative' }}>
                            {filteredContent.slice(visibleRange.start, visibleRange.end).map((line, i) => {
                                const actualIndex = visibleRange.start + i;
                                return (
                                    <div 
                                        key={actualIndex}
                                        style={{
                                            position: 'absolute',
                                            top: actualIndex * LINE_HEIGHT,
                                            left: 0,
                                            right: 0,
                                            height: LINE_HEIGHT,
                                        }}
                                        className={cn(
                                            "text-fg px-4 py-0.5 border-l-2 flex items-center",
                                            getLineClass(line)
                                        )}
                                    >
                                        <span className="truncate">{line || '\u00A0'}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col glass-panel rounded-xl overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-300">
            <div className="bg-white/5 px-4 py-3 border-b border-line flex justify-between items-center">
                <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 rounded-lg bg-black/40 flex items-center justify-center border border-line">
                        <ScrollText size={16} className="text-orange-400" />
                    </div>
                    <div>
                        <div className="text-xs text-fg-muted font-medium uppercase tracking-wider">Server Logs</div>
                        <div className="text-sm font-bold text-white flex items-center">
                            <span className="text-blue-400 mr-2">{activeServer?.name}</span>
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-fg-muted font-mono border border-line">
                                {logFiles.length} Dateien
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <div className="relative">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Dateien suchen..."
                            className="bg-black/40 border border-line-strong rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-fg-muted/50 focus:outline-none focus:border-blue-500/50 w-48"
                        />
                    </div>
                    <button
                        onClick={fetchLogFiles}
                        className="p-2 rounded-lg bg-black/40 border border-line-strong hover:bg-white/10 text-fg-muted hover:text-white transition-colors"
                        title="Aktualisieren"
                    >
                        <RefreshCw size={16} />
                    </button>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
                {loading ? (
                    <FunLoader />
                ) : filteredFiles.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-fg-muted">
                        <ScrollText size={48} className="mb-4 opacity-50" />
                        <p className="text-lg font-medium">Keine Log-Dateien gefunden</p>
                        <p className="text-sm">Der Server hat noch keine Logs erstellt</p>
                    </div>
                ) : (
                    <div className="grid gap-2">
                        {filteredFiles.map((file) => (
                            <button
                                key={file.name}
                                onClick={() => viewLogContent(file.name)}
                                className={cn(
                                    "w-full text-left p-4 rounded-xl border transition-all group",
                                    "bg-white/5 border-line hover:border-orange-500/30 hover:bg-orange-500/5",
                                    file.name === 'latest.log' && "border-blue-500/30 bg-blue-500/5"
                                )}
                            >
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <FileText size={20} className={cn(
                                            "transition-colors",
                                            file.name === 'latest.log' ? "text-blue-400" : "text-orange-400 group-hover:text-orange-300"
                                        )} />
                                        <div>
                                            <div className={cn(
                                                "font-medium",
                                                file.name === 'latest.log' ? "text-blue-400" : "text-white"
                                            )}>
                                                {file.name}
                                                {file.name === 'latest.log' && (
                                                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400 font-bold">
                                                        AKTUELL
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center space-x-4 text-xs text-fg-muted mt-1">
                                                <span className="flex items-center">
                                                    <HardDrive size={12} className="mr-1" />
                                                    {formatBytes(file.size)}
                                                </span>
                                                <span className="flex items-center">
                                                    <Calendar size={12} className="mr-1" />
                                                    {formatDate(file.modified)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="text-fg-muted group-hover:text-orange-400 transition-colors">
                                        <FileText size={16} />
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
});
